import { FC, useMemo } from 'react';
import { SportsbookTranslations } from '../../../sportsbook-text-keys';
import { SubscriptionFilters } from '../../../api/sportsbook-api-types';
import useEventSubscription from '../../event-subscription/use-event-subscription';
import { reduceEventsToLeaguesWithEvents } from '../../../utils/reduce-events-to-leagues';
import SportsbookGameCardGroup, {
	SkeletonGameCardGroup
} from '../../../components/game-card-group/sportsbook-game-card-group';
import { FeaturedContentTypeDto } from '../../../api/sbapi/sbapi-api-model';
import useSportsbookRoutes from '../../../hooks/use-sportsbook-routes';

type Props = {
	type: FeaturedContentTypeDto;
	eventCount?: number;
	filters: SubscriptionFilters;
	tag: string;
	translations: SportsbookTranslations;
	onHeaderClick?: () => void;
};

/**
 * Component for displaying a league with events as game cards. Use specifically for the explore page.
 *
 * @param type - The type of the featured content.
 * @param tag - The tag to determine origin of selection for tracking purposes.
 * @param eventCount - The expected number of events to display. Used for the skeleton loader.
 * @param filters - The filters to use for the event subscription.
 * @param preferredMarketTypeIds - The preferred market type ids to use for the game cards.
 */
const SportsbookExploreFeaturedLeague: FC<Props> = ({
	type,
	eventCount,
	filters,
	tag,
	translations,
	onHeaderClick
}) => {
	const { getLeaguePageLink } = useSportsbookRoutes();

	const subscriptionFilters = useMemo(() => {
		const result = { ...filters };
		// Remove flat market type filters if market type filters array is present.
		if (result.marketTypeFilters) {
			delete result.marketTypeIds;
			delete result.points;
			delete result.isPreferredMarket;
		}
		return result;
	}, [filters]);

	const { events, isInitialized, isLoading } = useEventSubscription({
		ref: `explore_${type}_${JSON.stringify(subscriptionFilters.leagueIds)}`,
		filters: subscriptionFilters,
		isEnabled: true
	});

	const league = useMemo(() => reduceEventsToLeaguesWithEvents(events).at(0), [events]);

	// Show nothing if there are no leagues or events.
	if (isInitialized && events.length === 0) {
		return null;
	}

	if (!isInitialized || (isLoading && events.length === 0)) {
		return (
			<SkeletonGameCardGroup
				title={league?.name || ''}
				sportIcon={league?.sportIcon}
				eventsCount={eventCount || league?.eventCount}
			/>
		);
	}

	return (
		<SportsbookGameCardGroup
			title={league?.name || ''}
			titleLink={league ? getLeaguePageLink(league.sportId, league.sportName, league.id, league.name) : ''}
			sportId={league?.sportId}
			events={events}
			translations={translations}
			onHeaderClick={onHeaderClick}
			tag={tag}
			sportIcon={league?.sportIcon}
		/>
	);
};

export default SportsbookExploreFeaturedLeague;
